import { type CodigoErroHermes } from '@varga/shared';

/**
 * Dicionário de erro do painel (UX-PAINEL-INTERNO §4.4 — "erro que explica").
 * A UI NUNCA mostra o `codigo` cru, nunca "Erro 400", nunca stack. Toda mensagem
 * de erro passa por aqui. Fonte dos códigos: `@varga/shared` (`erros.ts`).
 */

export interface VarsErro {
  /** nome do indicado / cliente sendo cadastrado */
  nome?: string;
  /** primeiro nome de quem indicou */
  indicador?: string;
}

const PRIMEIRO = (s: string | undefined): string => s?.trim().split(/\s+/)[0] ?? 'a pessoa';

type Entrada = string | ((v: VarsErro) => string);

const DICIONARIO: Record<CodigoErroHermes, Entrada> = {
  codigo_inexistente:
    'Esse código não existe. Confira as letras com o cliente — o código não tem I, O, 0 nem 1.',
  codigo_inativo: (v) =>
    `O código de ${PRIMEIRO(v.indicador)} está inativo ou vencido. A indicação não vai contar.`,
  nao_e_cliente_novo: (v) =>
    `${v.nome ?? 'A pessoa'} já fez serviço na Varga antes, então não pode ser indicado. O cadastro é feito, mas sem brinde de indicação.`,
  mesmo_contato: 'O telefone é o mesmo de quem indicou. Autoindicação não conta.',
  mesmo_documento:
    'O documento é o mesmo de quem indicou. Autoindicação não conta — o cliente foi cadastrado sem o documento.',
  ja_indicado: (v) =>
    `${v.nome ?? 'Esta pessoa'} já foi indicado por outra pessoa. Vale a primeira indicação.`,
  indicador_inativo: 'Quem indicou está com cadastro inativo. A indicação não vai contar.',
  campanha_encerrada:
    'O programa terminou em 07/07/2027. Indicações não geram mais brinde — mas o cadastro é feito normalmente.',
  documento_invalido: 'CPF/CNPJ inválido. Confira os números.',
  telefone_invalido: 'Telefone inválido. Precisa de DDD e 8 ou 9 dígitos.',
  conflito: 'Já existe uma OS com esse número e dados diferentes. Confira o número da OS.',
  validacao: 'Algum campo está incompleto ou fora do formato.',
  nao_autorizado: 'Você não tem permissão para essa ação.',
  nao_autenticado: 'E-mail ou senha incorretos.',
  nao_encontrado: 'Não encontramos o que você procura.',
  rate_limit: 'Muitas tentativas seguidas. Espere um instante.',
  colisao_codigo: 'Não foi possível gerar um código único agora. Tente confirmar de novo.',
  voucher_ja_entregue: 'Este voucher já foi retirado. Não pode ser retirado de novo.',
  interno: 'Erro no servidor. Tente de novo; se persistir, avise o suporte.',
  extracao_falhou: 'Não foi possível ler a OS. Confira os dados e preencha à mão.',
};

/** Erro de rede (sem resposta da API) — UX-PAINEL-INTERNO §1. */
export const MSG_ERRO_REDE = 'Não foi possível conectar. Verifique a internet e tente de novo.';

/** Sessão expirada no meio de uma tela — UX-PAINEL-INTERNO §1. */
export const MSG_SESSAO_EXPIRADA =
  'Sua sessão expirou. Entre de novo — o que você estava preenchendo foi guardado.';

export function mensagemDeErro(codigo: string, vars: VarsErro = {}): string {
  const entrada = DICIONARIO[codigo as CodigoErroHermes];
  if (!entrada) return DICIONARIO.interno as string;
  return typeof entrada === 'function' ? entrada(vars) : entrada;
}
